import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import logo from '../assets/images/logo.png';
import platformImg from '../assets/images/oil-platform.png';
import '../styles/auth.css';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import authService from '../services/authService';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  // Get token from URL path parameter
  const { token } = useParams();
  
  // Validate alphanumeric characters only
  const isValidPassword = (password) => {
    return /^[a-zA-Z0-9]+$/.test(password);
  };
  
  // Password strength checker - updated for alphanumeric only
  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (password.length >= 12) strength += 1; // Bonus for longer passwords
    if (/[A-Z]/.test(password)) strength += 1; // Has uppercase
    if (/[0-9]/.test(password)) strength += 1; // Has numbers
    if (/[a-z]/.test(password)) strength += 1; // Has lowercase
    
    // Cap the strength at 4 for consistency with UI
    setPasswordStrength(Math.min(strength, 4));
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    checkPasswordStrength(value);
  };
  
  const validateForm = () => {
    if (!token) {
      setError('Missing password reset token');
      return false;
    }
    
    if (!newPassword || !confirmPassword) {
      setError('All fields are required');
      return false;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    // Updated password validation
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    
    if (newPassword.length > 16) {
      setError('Password must not exceed 16 characters');
      return false;
    }
    
    if (!isValidPassword(newPassword)) {
      setError('Password must contain only letters and numbers (no special characters)');
      return false;
    }
    
    return true;
  };
  
  // Password strength indicator - updated descriptions
  const renderPasswordStrength = () => {
    const strengthText = ['Weak', 'Fair', 'Good', 'Strong'];
    const strengthColor = ['#dc3545', '#ffc107', '#0d6efd', '#198754'];
    
    if (newPassword.length === 0) return null;
    
    return (
      <div className="mt-1">
        <div className="d-flex align-items-center">
          <div className="me-2" style={{ width: '100%', height: '5px', backgroundColor: '#e9ecef' }}>
            {[...Array(4)].map((_, index) => (
              <div 
                key={index}
                style={{
                  width: '25%',
                  height: '100%',
                  display: 'inline-block',
                  backgroundColor: index < passwordStrength ? strengthColor[passwordStrength - 1] : 'transparent'
                }}
              />
            ))}
          </div>
          <small style={{ color: strengthColor[passwordStrength - 1] || '#6c757d' }}>
            {passwordStrength > 0 ? strengthText[passwordStrength - 1] : 'Weak'}
          </small>
        </div>
        {passwordStrength < 3 && (
          <small className="text-muted mt-1 d-block">
            Use 8-16 characters with uppercase, lowercase, and numbers for a stronger password
          </small>
        )}
        {newPassword.length > 0 && !isValidPassword(newPassword) && (
          <small className="text-danger mt-1 d-block">
            Only letters and numbers are allowed (no special characters)
          </small>
        )}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
      try {
          const response = await authService.resetPassword(token, newPassword);
          setSuccess(true);
        } catch (err) {
         setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
        } finally {
          setLoading(false);
        }
     
  };
  
  return (
    <div className="container-fluid login-container">
      <div className="row h-100">
        <div 
          className="col-md-8 p-0 platform-image"
          style={{ backgroundImage: `url(${platformImg})` }}
        ></div>
        
        <div className="col-md-4 login-form-container">
          <div className="ongc-logo">
            <img src={logo} alt="ONGC Logo" className="img-fluid" />
          </div>
          
          <div className="login-text">
            <h5>Reset Password</h5>
          </div>
          
          <div className="form-container">
            {error && (
              <div className="alert alert-danger d-flex align-items-center" role="alert">
                <AlertCircle size={18} className="me-2" />
                <div>{error}</div>
              </div>
            )}
            
            {success ? (
              <div className="text-center">
                <div className="alert alert-success d-flex align-items-center" role="alert">
                  <CheckCircle size={18} className="me-2" />
                  <div>Your password has been successfully reset.</div>
                </div>
                <Link to="/login" className="btn login-btn mt-3">
                  PROCEED TO LOGIN
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="newPassword" className="form-label">New Password</label>
                  <div className="input-group">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      className="form-control"
                      id="newPassword"
                      placeholder="8-16 characters, letters and numbers only"
                      value={newPassword}
                      onChange={handleNewPasswordChange}
                      maxLength={16}
                    />
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      aria-label="Toggle password visibility"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {renderPasswordStrength()}
                </div>
                
                <div className="mb-3">
                  <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                  <div className="input-group">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="form-control"
                      id="confirmPassword"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      maxLength={16}
                    />
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label="Toggle password visibility"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {confirmPassword && newPassword && confirmPassword !== newPassword && (
                    <small className="text-danger mt-1 d-block">Passwords do not match</small>
                  )}
                  {confirmPassword && newPassword && confirmPassword === newPassword && (
                    <small className="text-success mt-1 d-block">Passwords match</small>
                  )}
                </div>
                
                <button 
                  type="submit" 
                  className="btn login-btn mt-3 w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      PROCESSING...
                    </>
                  ) : 'SUBMIT'}
                </button>
                
                <div className="text-center mt-3">
                  <Link to="/login">Back to Login</Link>
                </div>
              </form>
            )}
          </div>
          
          <div className="copyright">
            Copyright © 2025 ONGC. All Rights Reserved
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;